import React from 'react';
import { useTranslate, useRefresh, ReferenceField } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import { H2, TextField, AccountsActions } from '~/components';
import { TextField as RaTextField } from '~/components/ra';
import BalancesTable from '~/components/ra/Accounts/BalancesTable';
import LimitsAndCounters from '~/components/ra/Accounts/LimitsAndCounters';
import { Section } from '~/layout';
import { time } from '~/utils';
import { l } from '../PnlAccountList';
// import PnlAccountEdit from '../PnlAccountEdit';
import EditSourceName from '~/components/EditSourceName';
import { useProductTemplates } from '~/hooks/useProductTemplates';
import { useEditMode } from '~/hooks/useEditModeContext';

const Details = (props: any) => {
  const { record } = props;
  const { editMode } = useEditMode();
  const isViewMode = editMode === 'VIEW';
  const translate = useTranslate();
  const refetch = useRefresh();
  const t = (key: string): string => translate(l(key));

  const classes = useStyles();

  const { data: productTemplates, isLoading } = useProductTemplates();
  const productTemplateAlias = productTemplates?.find(
    (item) => item.code === record?.productTemplate?.code
  );

  const isFallback = record?.productTemplate?.code === 'FALLBACK';

  if (!record) return null;
  return (
    <>
      <Grid container justify='space-between' className={classes.bottomSpacing}>
        <Grid item xs={12} className={classes.headerSection}>
          <Grid container justify='space-between' alignItems='flex-start'>
            {isFallback && (
              <Grid item xs={12}>
                <Alert severity='warning' style={{ marginBottom: '1em', marginTop: '0.5em' }}>
                  {t('productTemplateFallback')}
                </Alert>
              </Grid>
            )}
            <Grid item>
              <H2 topSpacing={0} bottomSpacing={2}>
                {t('systemInfo')}
              </H2>
            </Grid>
            <Grid item className='sectionActions'>
              {isViewMode && (
                <AccountsActions
                  status={record.status}
                  resource='tenant/accounts'
                  record={record}
                  refetch={refetch as any}
                />
              )}
              {/* <PnlAccountEdit permissions={permissions} record={record} /> */}
            </Grid>
          </Grid>
          <Section>
            <Grid container spacing={2}>
              {!isFallback && (
                <Grid item xs={4}>
                  <TextField loading={isLoading} label={t('productTemplateName')}>
                    {productTemplateAlias?.alias}
                  </TextField>
                </Grid>
              )}
              {!isFallback && (
                <Grid item xs={4}>
                  <TextField label={t('detailsProductTemplateCode')}>
                    {record.productTemplate?.code}
                  </TextField>
                </Grid>
              )}
              {!isFallback && (
                <Grid item xs={4}>
                  <TextField label={t('productTemplateCategory')}>
                    {record.productTemplate?.category}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={4}>
                {record.bankAccountId ? (
                  <ReferenceField
                    source='bankAccountId'
                    record={record}
                    reference='bank/accounts'
                    link={false}
                    addLabel={false}
                  >
                    <RaTextField
                      source='alias'
                      label={t('liquidityBucket')}
                      showLink={`/bank/accounts/${record.bankAccountId}/show`}
                    />
                  </ReferenceField>
                ) : (
                  <TextField label={t('liquidityBucket')} />
                )}
              </Grid>
              <Grid item xs={4}>
                <TextField label={t('currency')}>{record.currency}</TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField label={t('currentDay')}>{time(record.currentDay).format('l')}</TextField>
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>

      <Grid container justify='space-between' className={classes.bottomSpacing}>
        <Grid item xs={12} className={classes.headerSection}>
          <Grid container justify='space-between' alignItems='flex-start'>
            <Grid item>
              <H2 topSpacing={0} bottomSpacing={2}>
                {t('nameAliasAndDescription')}
              </H2>
            </Grid>
            <Grid item className='sectionActions'>
              {isViewMode && (
                <EditSourceName
                  source='tenant/accounts'
                  resource='tenant/accounts'
                  record={record}
                  refetch={refetch as any}
                />
              )}
            </Grid>
          </Grid>
          <Section>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField label={t('accountName')}>{record.name}</TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField label={t('alias')}>{record.alias}</TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField multiline label={t('description')}>
                  {record.description}
                </TextField>
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>
      {!isFallback && isViewMode && (
        <Grid container justify='space-between' className={classes.bottomSpacing}>
          <Grid item xs={12}>
            <H2 topSpacing={0} bottomSpacing={0}>
              {t('balancesAndCounters')}
            </H2>
            <Section>
              <Grid container spacing={2} className={classes.container}>
                <Grid item xs={12}>
                  {record.balances && record.balances.length > 0 && (
                    <BalancesTable balances={record.balances} accountProfileType='TENANT' />
                  )}
                </Grid>
                <Grid item xs={12}>
                  {record.id && <LimitsAndCounters id={record.id} />}
                </Grid>
              </Grid>
            </Section>
          </Grid>
        </Grid>
      )}
    </>
  );
};

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    paddingLeft: theme.spacing(4),
  },
  bottomSpacing: {
    paddingBottom: theme.spacing(4),
  },
  headerSection: {
    '& .sectionActions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& .sectionActions': {
        opacity: 1,
      },
    },
  },
}));

export default Details;
